import ProjectService from "@/services/ProjectService";

/**
 * Fallback accent palette, used only when the server does not send one. The
 * server palette wins whenever it is present so the client never offers a
 * color the server would reject.
 */
export const DEFAULT_PROJECT_COLORS = [
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#14b8a6", // teal
  "#64748b", // slate
];

const defaultState = () => ({
  // For the owner: every workspace of the account. For a team member: only the
  // workspaces they were granted access to (paused grants are left out), each
  // carrying the member's `role` there. Both in the owner's order.
  projects: [],
  activeProjectId: null,
  // True when the signed-in user owns the account the active workspace belongs
  // to. Members see the switcher too, but never create, manage, or billing.
  isOwner: false,
  allowance: { limit: 1, used: 1, planName: null, included: false, canCreate: false },
  colors: [],
  loaded: false,
  loading: false,
  // `{ [resource]: { account, workspace } }` — what the account holds against
  // what this workspace holds. Solution pages list one workspace but are
  // allowanced by the account, so the list length alone misstates the plan.
  resourceUsage: {},
  resourceUsageLoaded: false,
});

const state = defaultState();

const withAllowance = (allowance, used) => {
  const limit = Number(allowance.limit) || 0;
  return {
    ...allowance,
    used,
    canCreate: limit > 0 && used < limit,
  };
};

const mutations = {
  SET_PROJECTS(state, { workspaces, activeWorkspaceId, isOwner, allowance, colors }) {
    state.projects = workspaces || [];
    state.activeProjectId = activeWorkspaceId || null;
    state.isOwner = !!isOwner;
    if (allowance) state.allowance = allowance;
    if (colors) state.colors = colors;
    state.loaded = true;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_RESOURCE_USAGE(state, resources) {
    state.resourceUsage = resources || {};
    state.resourceUsageLoaded = true;
  },
  SET_ACTIVE_PROJECT(state, projectId) {
    state.activeProjectId = projectId;
  },
  ADD_PROJECT(state, project) {
    state.projects = [...state.projects, project];
    state.allowance = withAllowance(state.allowance, state.allowance.used + 1);
  },
  UPDATE_PROJECT(state, project) {
    state.projects = state.projects.map((item) =>
      item.id === project.id ? { ...item, ...project } : item
    );
  },
  REMOVE_PROJECT(state, projectId) {
    state.projects = state.projects.filter((item) => item.id !== projectId);
    state.allowance = withAllowance(state.allowance, Math.max(state.allowance.used - 1, 0));
  },
  RESET(state) {
    Object.assign(state, defaultState());
  },
};

const actions = {
  async loadProjects({ commit, state }, force = false) {
    if (state.loaded && !force) return;
    commit("SET_LOADING", true);
    try {
      const result = await ProjectService.getProjects();
      commit("SET_PROJECTS", result || {});
    } catch (error) {
      console.error("[Projects] Failed to load workspaces:", error);
    } finally {
      commit("SET_LOADING", false);
    }
  },
  /**
   * Counts for the usage meters. Failures are swallowed: a header that cannot
   * say what the account is using falls back to the workspace's own count,
   * which is what it showed before these numbers existed.
   */
  async loadResourceUsage({ commit, state }, force = false) {
    if (state.resourceUsageLoaded && !force) return;
    try {
      const result = await ProjectService.getResourceUsage();
      commit("SET_RESOURCE_USAGE", result?.resources);
    } catch (error) {
      console.error("[Projects] Failed to load resource usage:", error);
    }
  },
  async createProject({ commit }, { name, color }) {
    const project = await ProjectService.createProject(name, color);
    commit("ADD_PROJECT", project);
    return project;
  },
  async updateProject({ commit }, { id, name, color }) {
    const project = await ProjectService.updateProject(id, { name, color });
    // Some servers answer a PUT with `{ success: true }` rather than the
    // document; fall back to what was sent so the UI never blanks a name.
    commit("UPDATE_PROJECT", { id, name, color, ...(project && project.id ? project : {}) });
    return project;
  },
  async deleteProject({ commit }, projectId) {
    await ProjectService.deleteProject(projectId);
    commit("REMOVE_PROJECT", projectId);
  },
  async selectProject({ commit }, projectId) {
    await ProjectService.selectProject(projectId);
    commit("SET_ACTIVE_PROJECT", projectId);
  },
  resetProjects({ commit }) {
    commit("RESET");
  },
};

const getters = {
  projects: (state) => state.projects,
  /**
   * The workspace the session is standing in. Falls back to the first workspace
   * when the server did not name one, so the switcher always has a label.
   */
  activeProject: (state) =>
    state.projects.find((item) => item.id === state.activeProjectId) ||
    (state.activeProjectId ? null : state.projects[0] || null),
  activeProjectId: (state) => state.activeProjectId,
  /** The account's default workspace: the one that cannot be deleted. */
  defaultProject: (state) => state.projects.find((item) => item.isDefault) || null,
  projectById: (state) => (id) => state.projects.find((item) => item.id === id) || null,
  projectColors: (state) => (state.colors.length ? state.colors : DEFAULT_PROJECT_COLORS),
  projectAllowance: (state) => state.allowance,
  projectsLoaded: (state) => state.loaded,
  isProjectOwner: (state) => state.isOwner,
  canCreateProject: (state) => state.isOwner && state.allowance.canCreate,
  /**
   * Whoever can enter at least one workspace sees the switcher: the owner with
   * every workspace of the account, a team member with the workspaces they were
   * granted. For a member it renders even with a single workspace because it
   * doubles as the label for where you currently are, which is the answer to
   * "which client's content am I looking at?".
   *
   * An owner is asked it once the account holds the feature, or already has
   * more than the one workspace every account starts with. Without either, the
   * row named the lone default workspace and offered to create another —
   * advertising something the account has not bought.
   *
   * `included` rather than the limit: nearly every live plan is sold with
   * exactly one project, the 7-day trial among them, so a limit of 1 describes
   * both the plan someone just started and an account with no plan at all.
   * Reading the number took the feature away at the moment of purchase.
   *
   * A member who was granted nothing (or whose every grant is paused) gets an
   * empty list and no row at all; each page explains the missing permission in
   * its own content area.
   */
  showProjectSwitcher: (state) => {
    if (!state.loaded || state.projects.length === 0) return false;
    if (!state.isOwner) return true;
    return state.projects.length > 1 || !!state.allowance?.included;
  },
  /**
   * True once the account actually splits work across workspaces — the point at
   * which pills, "belongs to" copy, and per-project usage stop being jargon.
   */
  hasMultipleProjects: (state) => state.projects.length > 1,
  /**
   * `{ account, workspace }` for one resource, or null while that is unknown.
   * Null means "say nothing about the plan" rather than "zero".
   */
  resourceCounts: (state) => (resource) => {
    const entry = resource && state.resourceUsage[resource];
    if (!entry || !Number.isFinite(entry.account) || !Number.isFinite(entry.workspace)) return null;
    return { account: entry.account, workspace: entry.workspace };
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
